<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Contenus de la page publique d'une association — charte « apple » (Karim
 * 2026-10-18) : mot d'accueil, vidéo, puis un CHAPITRE par contenu
 * (cagnottes, événements, adhésions, boutique, QR codes, actualités), en
 * rangée de grandes cartes qui défile, comme « Cagnotte associative : loi
 * 1901, fiscalité, reversement » sur /cagnotte. Les fonds alternent gris /
 * blanc ; un contenu seul s'affiche en grande carte horizontale.
 *
 * Le nom du fichier (« colonne de droite ») est historique : la page tenait
 * sur deux colonnes avant la refonte.
 */
// Ville du lieu (§10) : « Saint-Denis, La Réunion », pas la ville seule.
import { villeCourteLieu } from "#shared/utils/lieuPublic";

type ModalSection = "campaigns" | "events" | "memberships" | "posts";

interface RightColumnOrg {
  is_verified: boolean;
  welcome_message: string | null;
  campaigns: any[];
  events: any[];
  membership_plans: any[];
  qr_codes: Array<{ id: string; title: string; file_id: string | null }>;
  posts: any[];
  campaigns_total: number;
  events_total: number;
  membership_plans_total: number;
  shops: Array<{
    id: string;
    title: string;
    slug: string;
    description: string | null;
    cover_image: string | null;
  }>;
  shops_total: number;
  posts_total: number;
  youtube_url: string | null;
}

const props = defineProps<{
  /** Slug de l'association — construit les liens « voir tout ». */
  slug: string;
  org: RightColumnOrg;
  youtubeEmbedUrl: string | null;
  formatAmount: (amount: number) => string;
  formatDate: (dateStr: string | null) => string | null;
  stripHtml: (html: string) => string;
  getImageUrl: (fileId: string | null, width?: number, height?: number) => string | null;
}>();

defineEmits<{
  openModal: [section: ModalSection];

  openPost: [post: any];
}>();

const { t } = useI18n({ useScope: "local" });
const { formatCurrency } = useFormatLangue();

// Fonds alternés gris / blanc, comptés depuis le DERNIER chapitre : il est
// blanc, pour que la bande grise « Informations » qui suit ne le prolonge pas.
const fonds = computed(() => {
  const o = props.org;
  const visibles = [
    o.campaigns.length > 0 && "campaigns",
    o.events.length > 0 && "events",
    o.membership_plans?.length > 0 && "memberships",
    o.shops.length > 0 && "shops",
    o.qr_codes.length > 0 && "qr-codes",
    o.posts.length > 0 && "posts",
  ].filter((v): v is string => !!v);
  return Object.fromEntries(
    visibles.map((id, i) => [id, (visibles.length - 1 - i) % 2 === 0 ? "blanc" : "gris"]),
  ) as Record<string, "blanc" | "gris">;
});

const prixAdhesion = (plan: any) => {
  const montant = Number(plan.min_price ?? plan.price);
  const prix = montant > 0 ? formatCurrency(montant) : t("association.rightColumn.gratuit");
  return Number(plan.price_count ?? 1) > 1 ? `${t("association.rightColumn.aPartirDe")} ${prix}` : prix;
};
</script>

<template>
  <div>
    <!-- Mot d'accueil : posé directement sur la page, sans cadre (Karim
         2026-07-23), en grand corps de lecture ; vidéo dessous. -->
    <div v-if="org.welcome_message || youtubeEmbedUrl" class="mx-auto max-w-[760px] space-y-12 px-4 pt-6 pb-16 sm:px-8 lg:pb-20">
      <!-- eslint-disable vue/no-v-html -->
      <!-- Reason: welcome_message est RICH-TEXT sanitizé côté backend Donoor -->
      <div v-if="org.welcome_message" class="mot-accueil pa-reveal prose prose-gray max-w-none" v-html="org.welcome_message" />
      <!-- eslint-enable vue/no-v-html -->
      <div v-if="youtubeEmbedUrl" class="pa-reveal relative aspect-video overflow-hidden rounded-[28px] bg-black">
        <iframe :src="youtubeEmbedUrl" :title="t('association.rightColumn.videoTitre')" class="h-full w-full" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen />
      </div>
    </div>

    <!-- Cagnottes -->
    <AssociationsPageChapitre
      v-if="org.campaigns.length > 0"
      id="section-campaigns"
      :fond="fonds.campaigns"
      :defilant="org.campaigns.length > 1"
      :titre="t('association.rightColumn.cagnottesTitre')"
      :nombre="org.campaigns_total"
      :texte="t('association.rightColumn.cagnottesTexte')"
      :lien-texte="org.campaigns_total > org.campaigns.length ? t('association.rightColumn.voirLesCagnottes', { n: org.campaigns_total }) : null"
      :lien-href="`/associations/${slug}/cagnottes`"
    >
      <AssociationsPageTuile
        v-for="campaign in org.campaigns"
        :key="campaign.id"
        :large="org.campaigns.length === 1"
        :to="`/c/${campaign.slug}`"
        :image="getImageUrl(campaign.image, 800, 640)"
        :alt="campaign.title"
        icone="i-lucide-heart-handshake"
        :titre="campaign.title"
        :description="campaign.description ? stripHtml(campaign.description) : null"
        contenir
      >
        <template #badge>
          <span v-if="campaign.is_verified" class="absolute top-4 left-4 flex size-9 items-center justify-center rounded-full bg-white/90 backdrop-blur">
            <UIcon name="i-lucide-badge-check" class="size-5 text-(--color-main)" />
          </span>
        </template>
        <template v-if="campaign.goal_amount > 0">
          <!-- 10 px (`h-2.5`), comme sur la page « Toutes les cagnottes » :
               à 6 px la barre se perdait dans la grande carte (Karim 2026-11-02). -->
          <div class="mb-2.5 h-2.5 overflow-hidden rounded-full bg-black/[0.08]">
            <div class="h-full rounded-full bg-(--color-main) transition-[width] duration-700" :style="{ width: `${Math.min(campaign.percent, 100)}%` }" />
          </div>
          <div class="flex items-baseline justify-between text-[15px]">
            <span class="font-bold text-[#1d1d1f]">{{ formatAmount(campaign.raised) }}</span>
            <span class="font-medium text-[#6e6e73]">{{ campaign.percent }} %</span>
          </div>
        </template>
        <p v-else class="text-[15px] text-[#6e6e73]">
          <span class="font-bold text-[#1d1d1f]">{{ formatAmount(campaign.raised) }}</span> {{ t("association.rightColumn.collectes") }}
        </p>
      </AssociationsPageTuile>
    </AssociationsPageChapitre>

    <!-- Événements -->
    <AssociationsPageChapitre
      v-if="org.events.length > 0"
      id="section-events"
      :fond="fonds.events"
      :defilant="org.events.length > 1"
      :titre="t('association.rightColumn.evenementsTitre')"
      :nombre="org.events_total"
      :texte="t('association.rightColumn.evenementsTexte')"
      :lien-texte="org.events_total > org.events.length ? t('association.rightColumn.voirLesEvenements', { n: org.events_total }) : null"
      :lien-href="`/associations/${slug}/billetterie`"
    >
      <AssociationsPageTuile
        v-for="event in org.events"
        :key="event.id"
        :large="org.events.length === 1"
        :to="`/b/${event.slug}`"
        :image="getImageUrl(event.cover_image, 800, 640)"
        :alt="event.title"
        icone="i-lucide-calendar-days"
        :surtitre="formatDate(event.start_date)"
        :titre="event.title"
        :description="event.description ? stripHtml(event.description) : null"
      >
        <p v-if="villeCourteLieu(event)" class="inline-flex items-center gap-1.5 text-[15px] font-medium text-[#6e6e73]">
          <UIcon name="i-lucide-map-pin" class="size-4" />{{ villeCourteLieu(event) }}
        </p>
      </AssociationsPageTuile>
    </AssociationsPageChapitre>

    <!-- Adhésions -->
    <AssociationsPageChapitre
      v-if="org.membership_plans?.length > 0"
      id="section-memberships"
      :fond="fonds.memberships"
      :defilant="org.membership_plans.length > 1"
      :titre="t('association.rightColumn.adhesionsTitre')"
      :nombre="org.membership_plans_total"
      :texte="t('association.rightColumn.adhesionsTexte')"
      :lien-texte="org.membership_plans_total > org.membership_plans.length ? t('association.rightColumn.voirLesAdhesions', { n: org.membership_plans_total }) : null"
      :lien-href="`/associations/${slug}/adhesions`"
    >
      <AssociationsPageTuile
        v-for="plan in org.membership_plans"
        :key="plan.id"
        :large="org.membership_plans.length === 1"
        :to="`/a/${plan.slug}`"
        :image="getImageUrl(plan.cover_image, 800, 640)"
        :alt="plan.title"
        icone="i-lucide-id-card"
        :surtitre="prixAdhesion(plan)"
        :titre="plan.title"
        :description="plan.description ? stripHtml(plan.description) : null"
      />
    </AssociationsPageChapitre>

    <!-- Boutique (2026-08-17) — pas de prix ici : une boutique contient N
         produits à N prix, un chiffre unique n'aurait aucun sens. -->
    <AssociationsPageChapitre
      v-if="org.shops.length > 0"
      id="section-shops"
      :fond="fonds.shops"
      :defilant="org.shops.length > 1"
      :titre="t('association.rightColumn.boutiqueTitre')"
      :nombre="org.shops_total"
      :texte="t('association.rightColumn.boutiqueTexte')"
      :lien-texte="org.shops_total > org.shops.length ? t('association.rightColumn.voirLesBoutiques', { n: org.shops_total }) : null"
      :lien-href="`/associations/${slug}/boutiques`"
    >
      <AssociationsPageTuile
        v-for="shop in org.shops"
        :key="shop.id"
        :large="org.shops.length === 1"
        :to="`/boutique/${shop.slug}`"
        :image="getImageUrl(shop.cover_image, 800, 640)"
        :alt="shop.title"
        icone="i-lucide-shopping-bag"
        :titre="shop.title"
        :description="shop.description ? stripHtml(shop.description) : null"
      />
    </AssociationsPageChapitre>

    <!-- QR Codes : petites cartes posées dans la colonne. -->
    <AssociationsPageChapitre
      v-if="org.qr_codes.length > 0"
      id="section-qr-codes"
      :fond="fonds['qr-codes']"
      :defilant="false"
      :titre="t('association.rightColumn.qrCodesTitre')"
      :nombre="org.qr_codes.length"
      :texte="t('association.rightColumn.qrCodesTexte')"
    >
      <div class="-mx-4 flex gap-4 overflow-x-auto px-4 pb-2 scrollbar-hide sm:mx-0 sm:flex-wrap sm:px-0">
        <div
          v-for="(qr, i) in org.qr_codes"
          :key="qr.id"
          class="qr pa-reveal w-44 shrink-0 rounded-[28px] p-5 text-center"
          :style="{ '--d': `${(i % 4) * 70}ms` }"
        >
          <div class="mx-auto mb-4 flex size-32 items-center justify-center rounded-2xl bg-white p-2.5">
            <img v-if="qr.file_id" :src="getImageUrl(qr.file_id, 260, 260)!" :alt="qr.title" class="h-full w-full object-contain" />
            <UIcon v-else name="i-lucide-qr-code" class="size-10 text-[#86868b]" />
          </div>
          <p class="line-clamp-2 text-[15px] font-semibold text-[#1d1d1f]">{{ qr.title }}</p>
        </div>
      </div>
    </AssociationsPageChapitre>

    <!-- Actualités : une carte ouvre l'article complet (fenêtre). -->
    <AssociationsPageChapitre
      v-if="org.posts.length > 0"
      id="section-posts"
      :fond="fonds.posts"
      :defilant="org.posts.length > 1"
      :titre="t('association.rightColumn.actualitesTitre')"
      :nombre="org.posts_total"
      :texte="t('association.rightColumn.actualitesTexte')"
      :lien-texte="org.posts_total > org.posts.length ? t('association.rightColumn.voirLesActualites', { n: org.posts_total }) : null"
      bouton-voir
      @voir="$emit('openModal', 'posts')"
    >
      <AssociationsPageTuile
        v-for="post in org.posts"
        :key="post.id"
        :large="org.posts.length === 1"
        :image="getImageUrl(post.image, 800, 640)"
        :alt="post.title"
        icone="i-lucide-newspaper"
        :surtitre="formatDate(post.date_created)"
        :titre="post.title"
        :description="post.content ? stripHtml(post.content) : null"
        @click="$emit('openPost', post)"
      />
    </AssociationsPageChapitre>

    <!-- Rien de publié (ni cagnotte, ni événement, ni adhésion, ni boutique, ni
         actualité) : fiche d'identité au lieu d'une page vide (Karim 28/10/2026). -->
    <AssociationsPageFicheIdentite
      v-if="org.campaigns.length === 0 && org.events.length === 0 && !org.membership_plans?.length && !org.shops?.length && org.posts.length === 0"
      :org="(org as any)"
    />
  </div>
</template>

<style scoped>
/* Mot d'accueil : grand corps de lecture, titres serrés. */
.mot-accueil {
  font-size: 19px;
  line-height: 1.6;
  color: #424245;
}
.mot-accueil :deep(h1),
.mot-accueil :deep(h2),
.mot-accueil :deep(h3) {
  color: #1d1d1f;
  letter-spacing: -0.02em;
}
.mot-accueil :deep(img) {
  border-radius: 20px;
}

/* QR codes : même couleur de carte que le chapitre (fond inversé). */
.qr {
  background: var(--fond-carte, #f5f5f7);
}
</style>

<i18n lang="json" src="../../../i18n/pages/components/associations-page/AssociationsPageRightColumn.json"></i18n>
